// File: @/components/media/media-library/FolderCard.tsx

import {useCallback} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {Folder, Share2} from 'lucide-react-native';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {AppDataMediaFolder} from '@/data/app-data-interfaces';
import {ThemeType} from '@/theming/baseTheme';
import {BaseCard} from '@/components/common/BaseCard';

interface FolderWithChildren extends AppDataMediaFolder {
    parentId?: string;
    children?: FolderWithChildren[];
}

interface FolderCardProps {
    folder: FolderWithChildren;
    itemCount: number;
    onPress: (folder: FolderWithChildren) => void;
}

export const FolderCard = ({folder, itemCount, onPress}: FolderCardProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    const handlePress = useCallback(() => {
        onPress(folder);
    }, [folder, onPress]);

    return (
        <BaseCard onPress={handlePress} activeOpacity={0.7} variant="flex">
            <View style={styles.folderImage}>
                <Folder size={40} color={folder.isShared ? theme.colors.primary : theme.colors.primary}/>
                {folder.isShared && (
                    <View style={styles.sharedBadge}>
                        <Share2 size={12} color="white"/>
                    </View>
                )}
                {itemCount > 0 && (
                    <View style={styles.folderIndicator}>
                        <Text style={styles.folderIndicatorText}>{t('media.folder.itemCount', {count: itemCount})}</Text>
                    </View>
                )}
            </View>
            <View style={styles.folderInfo}>
                <Text style={styles.folderTitle} numberOfLines={2}>{folder.name}</Text>
                <Text style={styles.folderDescription} numberOfLines={2}>
                    {t('media.folder.folderDescription', {count: itemCount})}
                </Text>
            </View>
        </BaseCard>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    folderImage: {
        height: 120,
        borderTopLeftRadius: theme.borderRadius.md,
        borderTopRightRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.secondary + '20',
        position: 'relative',
    },

    sharedBadge: {
        position: 'absolute',
        top: theme.spacing.xs,
        left: theme.spacing.xs,
        backgroundColor: theme.colors.primary,
        padding: 4,
        borderRadius: theme.borderRadius.sm,
    },

    folderIndicator: {
        position: 'absolute',
        top: theme.spacing.xs,
        right: theme.spacing.xs,
        backgroundColor: theme.colors.primary + 'E6',
        paddingHorizontal: theme.spacing.xs,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },

    folderIndicatorText: {
        color: 'white',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
    },

    folderInfo: {
        padding: theme.spacing.sm,
    },

    folderTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },

    folderDescription: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.textSecondary,
        lineHeight: 16,
    },
});
